import type { ActivityDefinition, Observation, ObservationDefinition } from "@medplum/fhirtypes";
import { getTRPCClient, syncKey } from "@repo/api";
import type { CloseOutExamInput } from "@repo/api/schemas";
import type { FindingComponent, FindingComponentAnswer, FindingState } from "@repo/domain";
import {
  asFindingId,
  asFindingRecordId,
  type CanonicalLineage,
  Exam,
  ExamDefinition,
  type ExamId,
  Finding,
  type FindingFacts,
  type FindingRecordId,
  type PatientId,
  PreconditionFailedError,
  type ProcedureId,
  type VisitId,
} from "@repo/domain";

import { eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { deriveObservationFields } from "../fhir/collections";
import { readExamDefinitionRow } from "../fhir/exam-definition-storage";
import { readExamRow } from "../fhir/exam-storage";
import { readFindingRow } from "../fhir/finding-storage";
import type { FindingDefinitionRow, ObservationRow } from "../fhir/types";
import { displayMeasuredValues } from "../lib/answered-value";
import { optimisticEditedFinding, optimisticRecordedFinding } from "../lib/finding-component-optimistic";
import { createAddExamFromVisitAction } from "./_add-exam-from-visit-action";
import {
  addExamMember,
  currentFindingDefinitionVersion,
  findingDefinitionRow,
  findingRow,
  observations,
} from "./_finding-rows";
import { projectExamDefinition } from "./exam-definition";
import { type FindingDefinitionDto, projectFindingDefinition } from "./finding-definition";

export type ExamFindingStatus = "Unanswered" | "Performed" | "DNP" | "UnableToPerform";
export interface ExamResponseOptionDto {
  responseKey: string;
  label: string;
  detail?: string;
  /** The finding's normal result: exclusive on a multiple-choice finding. */
  normal?: true;
}
export interface ExamFindingDto {
  kind: "Choice" | "Measured";
  /** A measured finding's declared components; empty for a choice. */
  components: readonly FindingComponent[];
  /** A performed measured finding's recorded values; empty otherwise. */
  values: readonly FindingComponentAnswer[];
  itemKey: CanonicalLineage;
  recordId?: FindingRecordId;
  procedureId?: ProcedureId;
  recordedVisitId?: VisitId;
  definitionVersion: number;
  label: string;
  status: ExamFindingStatus;
  cardinality: "Single" | "Multiple";
  responses: ExamResponseOptionDto[];
  responseKeys: string[];
  /**
   * What was answered, ready to show: a choice's selected response labels in
   * offered order, or a measured finding's values as one combined label.
   * Empty unless performed.
   */
  answerLabels: string[];
  comment?: string;
  prerequisiteItemKey?: CanonicalLineage;
  trackingRequested: boolean;
}
export interface ExamSectionDto {
  sectionId: string;
  label: string;
  findings: ExamFindingDto[];
}
export interface ExamAdministrationDto {
  examId: ExamId;
  patientId: PatientId;
  visitId: VisitId;
  definitionUrl: string;
  definitionVersion: string;
  definitionName: string;
  status: "preliminary" | "final";
  started: boolean;
  lastRecordedAt?: string;
  sections: ExamSectionDto[];
  accountedFindingCount: number;
  findingCount: number;
}
export interface ExamAdministrationFilters {
  patientId: PatientId;
  visitId?: VisitId;
}
export type ExamFindingRegistry = ReadonlyMap<string, FindingDefinitionDto>;
export function examFindingRegistry(rows: readonly FindingDefinitionRow[]): ExamFindingRegistry {
  return new Map(rows.map((row) => [row.findingDefinitionRef, projectFindingDefinition(row)]));
}
/** The answer-shape half of an exam finding row: what was offered, and what was recorded against it. */
export function examFindingAnswer(
  definition: FindingDefinitionDto,
  state: FindingState | undefined,
): Pick<
  ExamFindingDto,
  "kind" | "components" | "values" | "cardinality" | "responses" | "responseKeys" | "answerLabels"
> {
  const answer = state?.kind === "Performed" ? state.answer : undefined;
  if (definition.kind === "Measured") {
    const values = answer?.kind === "Measured" ? answer.values : [];
    const text = displayMeasuredValues(definition.components, values);
    return {
      kind: "Measured",
      components: definition.components,
      values,
      cardinality: "Single",
      responses: [],
      responseKeys: [],
      answerLabels: text ? [text] : [],
    };
  }
  const responses = [
    ...definition.options.map((option) => ({
      responseKey: option.optionId,
      label: option.label,
      ...(option.detail === undefined ? {} : { detail: option.detail }),
      ...(option.normal ? { normal: true as const } : {}),
    })),
    ...(definition.offerOther ? [{ responseKey: "other", label: "Other" }] : []),
  ];
  const responseKeys = answer?.kind === "Choice" ? [...answer.selections] : [];
  return {
    kind: "Choice",
    components: [],
    values: [],
    cardinality: definition.cardinality,
    responses,
    responseKeys,
    answerLabels: responses
      .filter((response) => responseKeys.includes(response.responseKey))
      .map((response) => response.label),
  };
}

export function projectExamAdministration(
  panel: Observation,
  definitionResource: ActivityDefinition,
  registry: ExamFindingRegistry,
  records: readonly FindingFacts[] = [],
): ExamAdministrationDto {
  const exam = readExamRow(panel);
  const definition = projectExamDefinition(definitionResource);
  const members = records.filter((record) => exam.members.includes(record.id) && record.status === "final");
  const sections = definition.sections.map((section) => ({
    ...section,
    findings: section.findings.map((placement): ExamFindingDto => {
      const recorded = members.find((member) => member.definition.url === placement.findingDefinitionUrl);
      const current = [...registry.values()]
        .filter((candidate) => candidate.definitionUrl === placement.findingDefinitionUrl)
        .sort((a, b) => b.version - a.version)[0];
      const offered = recorded ? registry.get(`${recorded.definition.url}|${recorded.definition.version}`) : current;
      if (!offered)
        throw new Error(
          `Finding definition '${placement.findingDefinitionUrl}|${recorded?.definition.version ?? "current"}' is not loaded`,
        );
      return {
        itemKey: asFindingId(placement.findingDefinitionUrl),
        definitionVersion: offered.version,
        recordId: recorded?.id,
        procedureId: recorded?.procedure,
        recordedVisitId: recorded?.visit,
        // The name is lineage-level and lives on the current revision, so a pinned old revision shows today's name.
        label: (current ?? offered).name,
        ...examFindingAnswer(offered, recorded?.state),
        status: recorded?.state.kind === "DidNotPerform" ? "DNP" : (recorded?.state.kind ?? "Unanswered"),
        comment: recorded?.comment,
        prerequisiteItemKey: placement.prerequisiteFindingUrl
          ? asFindingId(placement.prerequisiteFindingUrl)
          : undefined,
        trackingRequested: recorded?.trackedForProgress ?? false,
      };
    }),
  }));
  const findings = sections.flatMap((section) => section.findings);
  return {
    examId: exam.id,
    patientId: exam.patient,
    visitId: exam.visit,
    definitionUrl: exam.definition.url,
    definitionVersion: exam.definition.version,
    definitionName: definition.name,
    status: exam.status,
    started: members.length > 0,
    lastRecordedAt: members
      .map((member) => member.recordedAt)
      .sort()
      .at(-1),
    sections,
    accountedFindingCount: members.filter((member) => member.state.kind !== "Unanswered").length,
    findingCount: findings.length,
  };
}
export function useExamAdministrations(filters: ExamAdministrationFilters | undefined): ExamAdministrationDto[] {
  const observations = useCollection("observation");
  const definitions = useCollection("activityDefinition");
  const findingDefinitions = useCollection("findingDefinition");
  const rows = useLiveQuery(
    (q) =>
      filters
        ? q
            .from({ row: observations })
            .where(({ row }) => eq(row.patientId, filters.patientId))
            .select(({ row }) => ({ row }))
        : undefined,
    [observations, filters?.patientId],
  );
  const catalog = useLiveQuery(
    (q) => (filters ? q.from({ row: definitions }).select(({ row }) => ({ row })) : undefined),
    [definitions, filters?.patientId],
  );
  const findingCatalog = useLiveQuery(
    (q) => (filters ? q.from({ row: findingDefinitions }).select(({ row }) => ({ row })) : undefined),
    [findingDefinitions, filters?.patientId],
  );
  const registry = examFindingRegistry((findingCatalog.data ?? []).map(({ row }) => row));
  const patientRows = (rows.data ?? []).map(({ row }) => row);
  const records = patientRows.filter((row) => row.findingInstance).map(readFindingRow);
  return patientRows
    .filter((row) => row.examInstance)
    .flatMap((panel) => {
      const exam = readExamRow(panel);
      if (filters?.visitId && exam.visit !== filters.visitId) return [];
      const definition = catalog.data?.find(
        ({ row }) => row.url === exam.definition.url && row.version === exam.definition.version,
      )?.row;
      if (!definition || exam.members.some((id) => !records.some((record) => record.id === id))) return [];
      if (
        records.some(
          (record) =>
            exam.members.includes(record.id) &&
            record.status === "final" &&
            !registry.has(`${record.definition.url}|${record.definition.version}`),
        )
      )
        return [];
      const placements = projectExamDefinition(definition).sections.flatMap((section) => section.findings);
      if (
        placements.some(
          (placement) =>
            ![...registry.values()].some((finding) => finding.definitionUrl === placement.findingDefinitionUrl),
        )
      )
        return [];
      return [projectExamAdministration(panel, definition, registry, records)];
    })
    .sort((a, b) => a.definitionName.localeCompare(b.definitionName));
}
export function useExamAdministration(
  scope: { examId: ExamId; patientId: PatientId } | undefined,
): ExamAdministrationDto | undefined {
  return useExamAdministrations(scope).find((exam) => exam.examId === scope?.examId);
}
type CloseOutPlan = Pick<CloseOutExamInput, "examId" | "visitId" | "clinician" | "confirmedDidNotPerform">;
interface StagedCloseOut {
  panel: ObservationRow;
  exam: Exam;
  definition: ExamDefinition;
  members: Finding[];
  recordedAt: string;
  written: Map<string, { row: Observation; created: boolean }>;
}

function stageConfirmedDidNotPerform(input: CloseOutPlan): StagedCloseOut {
  const panel = observations().get(input.examId);
  if (!panel) throw new Error("Exam is not loaded");
  const exam = Exam.restore(readExamRow(panel));
  const { definition: pinned, members: memberIds, patient } = exam.facts;
  const resource = [...getDataStoreClient().getCollection("activityDefinition").values()].find(
    (row) => row.url === pinned.url && row.version === pinned.version,
  );
  if (!resource) throw new Error(`Exam definition '${pinned.url}|${pinned.version}' is not loaded`);
  const definition = new ExamDefinition(readExamDefinitionRow(resource));
  const members = memberIds.map((id) => Finding.restore(readFindingRow(findingRow(id))));
  const staged: StagedCloseOut = {
    panel,
    exam,
    definition,
    members,
    recordedAt: new Date().toISOString(),
    written: new Map(),
  };
  for (const { id, definitionUrl, expectedVersion, comment } of input.confirmedDidNotPerform ?? []) {
    const offered = findingDefinitionRow({ url: definitionUrl, version: String(expectedVersion) });
    const commented = members.find(
      (member) =>
        member.facts.status === "final" &&
        member.facts.state.kind === "Unanswered" &&
        member.facts.definition.url === definitionUrl,
    );
    if (commented) {
      editStaged(staged, commented, offered, {
        ...commented.facts,
        clinician: input.clinician,
        recordedAt: staged.recordedAt,
        state: { kind: "DidNotPerform" },
        ...(comment === undefined ? {} : { comment }),
      });
      continue;
    }
    addStaged(
      staged,
      optimisticRecordedFinding(
        {
          id: asFindingRecordId(id),
          patient,
          visit: input.visitId,
          clinician: input.clinician,
          recordedAt: staged.recordedAt,
          state: { kind: "DidNotPerform" },
          ...(comment === undefined ? {} : { comment }),
        },
        offered,
      ),
    );
  }
  return staged;
}

function editStaged(
  staged: StagedCloseOut,
  member: Finding,
  offered: ObservationDefinition,
  recording: Parameters<typeof optimisticEditedFinding>[2],
): void {
  const prior = staged.written.get(member.id);
  const next = optimisticEditedFinding(prior?.row ?? findingRow(member.id), offered, recording);
  staged.written.set(member.id, { row: next, created: prior?.created ?? false });
  staged.members[staged.members.indexOf(member)] = Finding.restore(readFindingRow(next));
}

function addStaged(staged: StagedCloseOut, row: Observation): void {
  const finding = Finding.restore(readFindingRow(row));
  staged.exam.addFinding(staged.definition, finding, staged.members);
  staged.members.push(finding);
  staged.written.set(finding.id, { row, created: true });
}

function cascadeTargets(staged: StagedCloseOut) {
  return staged.exam.prerequisiteCascade(staged.definition, staged.members).map((cascade) => ({
    ...cascade,
    existing: staged.members.find(
      (member) => member.facts.status === "final" && member.facts.definition.url === cascade.finding,
    ),
  }));
}

/** The findings close-out will create from a skipped prerequisite, each pinned to the revision offered and needing a client-minted id. */
export function prerequisiteCascadeToRecord(input: CloseOutPlan): { definitionUrl: string; expectedVersion: number }[] {
  return cascadeTargets(stageConfirmedDidNotPerform(input))
    .filter((target) => !target.existing)
    .map((target) => ({
      definitionUrl: target.finding,
      expectedVersion: currentFindingDefinitionVersion(target.finding),
    }));
}

function recordCloseOut(input: CloseOutExamInput): ObservationRow {
  const staged = stageConfirmedDidNotPerform(input);
  for (const { finding, state, existing } of cascadeTargets(staged)) {
    if (existing) {
      editStaged(staged, existing, findingDefinitionRow(existing.facts.definition), {
        ...existing.facts,
        clinician: input.clinician,
        recordedAt: staged.recordedAt,
        state,
      });
      continue;
    }
    const entry = input.prerequisiteCascade?.find((candidate) => candidate.definitionUrl === finding);
    if (!entry) throw new PreconditionFailedError(`Cascaded finding '${finding}' was not offered by the client`);
    addStaged(
      staged,
      optimisticRecordedFinding(
        {
          id: asFindingRecordId(entry.id),
          patient: staged.exam.facts.patient,
          visit: input.visitId,
          clinician: input.clinician,
          recordedAt: staged.recordedAt,
          state,
        },
        findingDefinitionRow({ url: finding, version: String(entry.expectedVersion) }),
      ),
    );
  }
  for (const [id, { row, created }] of staged.written) {
    if (created) {
      observations().insert({ ...row, ...deriveObservationFields(row) });
      addExamMember(syncKey(staged.panel), id);
      continue;
    }
    const prior = findingRow(id);
    observations().update(syncKey(prior), (draft) => {
      Object.assign(draft, row);
    });
  }
  return staged.panel;
}
export const closeOutExam = defineWrite<CloseOutExamInput>({
  optimistic: (input) => {
    const panel = recordCloseOut(input);
    observations().update(syncKey(panel), (draft) => {
      draft.status = "final";
    });
  },
  persist: (input, options) => getTRPCClient().exam.closeOut.mutate(input, options),
});
export const addExamFromVisit = createAddExamFromVisitAction({
  appointments: () => getDataStoreClient().getCollection("appointment"),
  exams: () => getDataStoreClient().getCollection("observation"),
  definitions: () => [...getDataStoreClient().getCollection("activityDefinition").values()],
  mutate: (input, options) => getTRPCClient().exam.addFromVisit.mutate(input, options),
});
